/**
 * ErrorHandler Utility
 * Provides user-friendly error messages and troubleshooting tips
 * 
 * Requirements: 1.5
 */

import { ToastManager } from '../components/ToastContainer';

export interface ErrorContext {
  component?: string;
  action?: string;
  details?: any;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private toastManager: ToastManager;

  private constructor() {
    this.toastManager = ToastManager.getInstance();
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Handle camera access errors
   * Requirement 1.5: Clear error messages with troubleshooting steps
   */
  handleCameraError(error: Error): void {
    let message = 'Camera access failed';
    let troubleshooting: string[] = [];

    // Detect iOS devices
    const isIOS = /iPhone|iPad/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !isChrome;

    if (error.message.includes('Permission denied') || error.message.includes('NotAllowedError')) {
      message = 'Camera permission denied';
      if (isIOS) {
        troubleshooting = [
          `Go to Settings > ${isSafari ? 'Safari' : 'Chrome'} > Camera and enable access`,
          'Refresh this page after enabling camera access',
          'Make sure no other app is using the camera',
          'Try closing and reopening your browser',
        ];
      } else {
        troubleshooting = [
          'Check your browser settings and allow camera access',
          'Look for a camera icon in the address bar and click "Allow"',
          'Try refreshing the page and granting permission when prompted',
          'Make sure no other app is using the camera',
        ];
      }
    } else if (error.message.includes('not found') || error.message.includes('NotFoundError')) {
      message = 'No camera detected';
      troubleshooting = [
        'Make sure your device has a camera',
        'Check if the camera is properly connected',
        'Try using a different browser',
        'Restart your device and try again',
      ];
    } else if (error.message.includes('in use') || error.message.includes('NotReadableError')) {
      message = 'Camera is already in use';
      troubleshooting = [
        'Close other apps that might be using the camera',
        'Close other browser tabs that might be accessing the camera',
        'Restart your browser and try again',
        isIOS ? 'Double-tap home button and swipe up on camera apps to close them' : 'Check Task Manager for camera applications',
      ];
    } else if (error.message.includes('HTTPS') || error.message.includes('secure')) {
      message = 'Secure connection required';
      troubleshooting = [
        'Camera access requires HTTPS (secure connection)',
        'Make sure the URL starts with "https://"',
        'Contact support if you\'re seeing this on the live site',
      ];
    } else if (error.message.includes('OverconstrainedError') || error.message.includes('constraints')) {
      message = 'Camera settings not supported';
      troubleshooting = [
        'Your camera doesn\'t support the requested settings',
        'Try switching to the front camera',
        'Try using a different browser',
        'Update your browser to the latest version',
      ];
    } else {
      troubleshooting = [
        'Try refreshing the page',
        'Check if your browser supports camera access',
        'Try using a different browser (Chrome, Safari, or Firefox)',
        'Make sure you\'re using HTTPS (not HTTP)',
        isIOS ? 'Try switching between Safari and Chrome' : 'Clear browser cache and cookies',
      ];
    }

    this.toastManager.error(message, troubleshooting, 12000); // Longer timeout for camera issues
  }

  /**
   * Handle network errors
   * Requirement 14.1: Network resilience
   */
  handleNetworkError(error: Error): void {
    let message = 'Network error';
    let troubleshooting: string[] = [];

    if (error.message.includes('timeout')) {
      message = 'Request timed out';
      troubleshooting = [
        'Check your internet connection',
        'Try moving to an area with better signal',
        'Disable VPN if you\'re using one',
        'Try again in a few moments',
      ];
    } else if (error.message.includes('offline') || error.message.includes('Network error')) {
      message = 'No internet connection';
      troubleshooting = [
        'Check if you\'re connected to WiFi or mobile data',
        'Try turning airplane mode off',
        'Check if other apps can access the internet',
        'Restart your device\'s network connection',
      ];
    } else if (error.message.includes('429') || error.message.includes('Too many requests')) {
      message = 'Too many requests';
      troubleshooting = [
        'Please wait a moment before trying again',
        'The server is busy, try again in 30 seconds',
      ];
    } else {
      troubleshooting = [
        'Check your internet connection',
        'Try refreshing the page',
        'Contact support if the problem persists',
      ];
    }

    this.toastManager.error(message, troubleshooting, 8000);
  }

  /**
   * Handle authentication errors
   * Requirement 10.3: Authentication validation
   */
  handleAuthError(error: Error): void {
    let message = 'Authentication failed';
    let troubleshooting: string[] = [];

    if (error.message.includes('expired') || error.message.includes('token')) {
      message = 'Session expired';
      troubleshooting = [
        'Your session has expired, please log in again',
        'Click the logout button and log back in',
      ];
    } else if (error.message.includes('Invalid credentials')) {
      message = 'Invalid username or password';
      troubleshooting = [
        'Check your username and password',
        'Make sure Caps Lock is off',
        'Try resetting your password if you forgot it',
      ];
    } else {
      troubleshooting = [
        'Try logging out and logging back in',
        'Clear your browser cache and cookies',
        'Contact support if the problem persists',
      ];
    }

    this.toastManager.error(message, troubleshooting, 10000);
  }

  /**
   * Handle upload/classification errors
   * Requirement 4.5: Error handling
   */
  handleUploadError(error: Error): void {
    let message = 'Upload failed';
    let troubleshooting: string[] = [];

    if (error.message.includes('Invalid image')) {
      message = 'Invalid image format';
      troubleshooting = [
        'Make sure the image is in JPEG or PNG format',
        'Try capturing a new frame',
        'Check if the camera is working properly',
      ];
    } else if (error.message.includes('too large') || error.message.includes('413')) {
      message = 'Image too large';
      troubleshooting = [
        'The image exceeds the 5MB size limit',
        'Try reducing the camera resolution in settings',
        'Contact support if the problem persists',
      ];
    } else if (error.message.includes('Server error') || error.message.includes('500')) {
      message = 'Server error';
      troubleshooting = [
        'The server is experiencing issues',
        'Please try again in a few moments',
        'Contact support if the problem persists',
      ];
    } else {
      troubleshooting = [
        'Check your internet connection',
        'Try again in a few moments',
        'Make sure you\'re logged in',
      ];
    }

    this.toastManager.error(message, troubleshooting, 8000);
  }

  /**
   * Handle generic errors
   */
  handleError(error: Error, context?: ErrorContext): void {
    console.error('Error:', error, context);

    // Route to specific handlers based on context
    if (context?.component === 'camera') {
      this.handleCameraError(error);
    } else if (context?.component === 'network' || context?.component === 'upload') {
      this.handleNetworkError(error);
    } else if (context?.component === 'auth') {
      this.handleAuthError(error);
    } else if (context?.component === 'classification') {
      this.handleUploadError(error);
    } else {
      // Generic error
      this.toastManager.error(
        error.message || 'An unexpected error occurred',
        [
          'Try refreshing the page',
          'Check your internet connection',
          'Contact support if the problem persists',
        ],
        8000
      );
    }
  }

  /**
   * Show success message
   */
  showSuccess(message: string, duration?: number): void {
    this.toastManager.success(message, duration);
  }

  /**
   * Show warning message
   */
  showWarning(message: string, troubleshooting?: string[], duration?: number): void {
    this.toastManager.warning(message, troubleshooting, duration);
  }

  /**
   * Show info message
   */
  showInfo(message: string, duration?: number): void {
    this.toastManager.info(message, duration);
  }

  /**
   * Show error message
   */
  showError(message: string, troubleshooting?: string[], duration?: number): void {
    this.toastManager.error(message, troubleshooting, duration);
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    this.toastManager.dismissAll();
  }
}

export default ErrorHandler;
